/**
 * Afrikaans: Insights
 *
 * What this file covers: Chat insights: AI reports on a period's chats (what customers asked about, where chats stopped, what went unanswered, the next document to add, and advice on the chatbot, marketing and demand), their filters, downloads and the monthly report.
 *
 * Tone: Clear and encouraging, for business owners reading about their own customers. Say what a figure means in plain words; never promise more than the AI found, and be exact about what a report costs.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const insights: Catalog = {
  'insights.title': 'Insigte',
  'insights.description': 'KI lees jou gesprekke oor \'n tydperk wat jy kies en gee terugvoer: waaroor mense gevra het, waar gesprekke gestop het, wat onbeantwoord gebly het, wat om by jou kennisbasis te voeg, en idees vir jou kletsbot en jou bemarking.',
  'insights.period': '{{from}} tot {{to}}',
  'insights.minutes': '{{n}} min',
  'insights.hours': '{{n}} h',

  'insights.new.button': 'Nuwe verslag',
  'insights.new.title': 'Nuwe insigverslag',
  'insights.new.period.label': 'Tydperk',
  'insights.new.period.last': 'Laaste {{n}} dae',
  'insights.new.period.custom': 'Datums',
  'insights.new.period.from': 'Van',
  'insights.new.period.to': 'Tot',
  'insights.new.period.missing': 'Kies albei datums.',
  'insights.new.period.order': 'Die eerste datum moet voor die tweede kom.',
  'insights.new.period.future': 'Die tydperk kan nie na vandag eindig nie.',
  'insights.new.period.long': '\'n Verslag dek hoogstens {{max}} dae.',
  'insights.new.channels.label': 'Kanale',
  'insights.new.channels.hint': 'Laat almal oop om elke kanaal in te sluit.',
  'insights.new.estimate.chats': '{{chats}} gesprekke in hierdie tydperk, {{readable}} met iets wat \'n kliënt geskryf het.',
  'insights.new.estimate.reused': '{{count}} daarvan is deur \'n vorige verslag gelees en het nie verander nie, so hulle is gratis.',
  'insights.new.estimate.sampled': 'Daar is meer gesprekke as wat een verslag lees, so \'n regverdige steekproef van {{read}} word gelees (jou grens is {{max}}). Die tellings word uit elke gesprek bereken.',
  'insights.new.depth.label': 'Hoe diep dit lees',
  'insights.new.focus.label': 'Jou vrae (opsioneel)',
  'insights.new.focus.hint': 'Tot 5 vrae, een per reël, elk tot 200 karakters. Die verslag tel en beantwoord hulle, byvoorbeeld: Hoeveel ouers het oor vervoer gevra?',
  'insights.new.focus.tooMany': 'Vra hoogstens 5 vrae.',
  'insights.new.focus.tooLong': 'Hou elke vraag tot 200 karakters.',
  'insights.new.notice': 'Dit neem \'n paar minute (sowat {{minutes}}). KI lees jou gesprekke, en dit kos sowat {{amount}}, wat van jou krediete afgetrek word terwyl dit loop. Jy kan hierdie bladsy verlaat: ons e-pos jou en stuur \'n kennisgewing wanneer die verslag gereed is.',
  'insights.new.noticeUnpriced': '\'n bedrag wat ons nou nie kon bereken nie',
  'insights.new.start': 'Begin verslag',
  'insights.new.started': 'Jou verslag het begin. Ons laat jou weet wanneer dit gereed is.',

  'insights.depth.recommended': 'Aanbeveel',
  'insights.depth.standard.name': 'Standaard',
  'insights.depth.standard.about': '\'n Vinnige, goedkoop lees van elke gesprek, met \'n deeglike verslag. Reg vir die meeste verslae.',
  'insights.depth.thorough.name': 'Deeglik',
  'insights.depth.thorough.about': 'Elke gesprek word deur die sterker model gelees, vir skerper besonderhede oor wat elke persoon wou hê.',
  'insights.depth.expert.name': 'Kundig',
  'insights.depth.expert.about': 'Die sterkste model skryf die verslag, vir die diepste raad.',
  'insights.depth.models': '{{reader}} lees, {{writer}} skryf',
  'insights.depth.price': 'Sowat {{amount}}',
  'insights.depth.unpriced': 'Prys nou nie bekend nie',
  'insights.depth.unavailable': 'Word tans nie aangebied nie.',
  'insights.depth.fallback': 'Een van sy modelle is afgeskakel, so \'n ander staan in.',
  'insights.depth.short': 'Daar is nie genoeg krediet vir hierdie diepte nie.',

  'insights.running.title': 'Jou verslag is op pad',
  'insights.running.stages': 'Vordering',
  'insights.running.queued': 'Wag om te begin.',
  'insights.running.gather': 'Tel jou gesprekke.',
  'insights.running.reading': 'Lees gesprekke: {{done}} van {{total}}.',
  'insights.running.read': 'Lees jou gesprekke.',
  'insights.running.organise': 'Sorteer wat mense gevra het in onderwerpe.',
  'insights.running.write': 'Skryf die verslag.',
  'insights.running.done': 'Rond af.',
  'insights.running.leave': 'Jy kan hierdie bladsy verlaat. Ons e-pos jou en stuur \'n kennisgewing wanneer dit gereed is.',
  'insights.running.cancel': 'Kanselleer verslag',
  'insights.running.cancelTitle': 'Kanselleer hierdie verslag?',
  'insights.running.cancelBody': 'Dit stop nou. Net wat dit reeds gelees het, word gehef.',
  'insights.running.cancelled': 'Die verslag is gekanselleer.',
  'insights.stage.gather': 'Tel gesprekke',
  'insights.stage.read': 'Lees gesprekke',
  'insights.stage.organise': 'Sorteer onderwerpe',
  'insights.stage.write': 'Skryf verslag',

  'insights.status.queued': 'Wag',
  'insights.status.running': 'Loop',
  'insights.status.done': 'Gereed',
  'insights.status.failed': 'Misluk',
  'insights.status.cancelled': 'Gekanselleer',
  'insights.status.skipped': 'Oorgeslaan',

  'insights.list.empty': 'Nog geen verslae nie',
  'insights.list.emptyHint': 'Druk Nuwe verslag om te sien waaroor jou kliënte gevra het en hoe jou kletsbot gevaar het.',
  'insights.list.monthly': 'Maandelikse verslag',
  'insights.list.chats': '{{count}} gesprekke',
  'insights.list.answered': '{{pct}}% deur die kletsbot beantwoord',
  'insights.list.unanswered': '{{count}} onbeantwoord gelaat',
  'insights.list.cost': 'Koste {{amount}}',
  'insights.list.delete': 'Skrap verslag',
  'insights.list.deleteTitle': 'Skrap hierdie verslag?',
  'insights.list.deleteBody': 'Die verslag en sy aflaaie word verwyder. Jou gesprekke word nie verander nie.',
  'insights.list.deleted': 'Die verslag is geskrap.',

  'insights.settings.title': 'Maandelikse verslag',
  'insights.settings.note': '\'n Verslag oor die vorige maand, op die 1ste van elke maand. Af totdat jy dit aanskakel.',
  'insights.settings.monthly': 'Maak elke maand \'n verslag',
  'insights.settings.monthlyHint': 'Dit gebruik die diepte, kanale en vrae hieronder, en word oorgeslaan wanneer jou krediet te min is.',
  'insights.settings.next': 'Volgende maandelikse verslag: {{when}}',
  'insights.settings.depth': 'Diepte',
  'insights.settings.depthHint': 'Hoe diep die maandelikse verslag lees. By elke verslag wat jy self begin, kies jy weer.',
  'insights.settings.max': 'Gesprekke per verslag',
  'insights.settings.maxHint': '50 tot 2 000. Bo dit word \'n regverdige steekproef gelees, wat die koste laag hou.',
  'insights.settings.maxRange': 'Kies \'n getal van 50 tot 2 000.',
  'insights.settings.saved': 'Gestoor.',
  'insights.settings.language': 'Taal van die verslag',
  'insights.settings.languageHint': 'Die maandelikse verslag word in hierdie taal geskryf. \'n Verslag wat jy self begin, word geskryf in die taal waarin jou konsole is.',
  'insights.settings.languageEn': 'Engels',
  'insights.settings.languageAf': 'Afrikaans',

  'insights.error.credits': 'Daar is nie genoeg krediet vir hierdie verslag nie. Laai op en probeer weer.',
  'insights.error.active': '\'n Verslag is reeds op pad. Wag dat dit klaarmaak, of kanselleer dit.',
  'insights.error.nothing': 'Geen gesprek in hierdie tydperk het iets wat \'n kliënt geskryf het nie, so daar is niks om te lees nie.',
  'insights.error.notDone': 'Hierdie verslag is nog nie gereed nie.',
  'insights.error.forbidden': 'Net die eienaar of \'n admin kan Insigte gebruik.',

  'insights.report.title': 'Insigte: {{period}}',
  'insights.report.back': 'Alle verslae',
  'insights.report.read': '{{read}} van {{chats}} gesprekke gelees',
  'insights.report.readSampled': '≈ {{read}} van {{chats}} gesprekke gelees (\'n steekproef)',
  'insights.report.sampledNote': '\'n Regverdige steekproef van {{read}} van {{chats}} gesprekke is gelees. Onderwerptellings kom uit daardie steekproef; die ander syfers tel elke gesprek.',
  'insights.report.download': 'Laai af',
  'insights.report.pdf': 'Laai PDF af',
  'insights.report.xlsx': 'Laai Excel af',
  'insights.report.again': 'Doen weer',
  'insights.report.whole': 'Deur KI geskryf vir die hele verslag: dit verander nie met die filters nie.',
  'insights.report.depthNote': 'Diepte: {{depth}}. Syfers word getel; die paragrawe en raad word deur KI daaruit geskryf.',
  'insights.report.missing': 'Verslag nie gevind nie',
  'insights.report.missingHint': 'Dit is dalk geskrap.',
  'insights.report.loadFailed': 'Die verslag kon nie gelaai word nie',
  'insights.report.failed': 'Hierdie verslag kon nie klaarmaak nie. Niks meer is gehef nie. Doen dit weer.',
  'insights.report.cancelled': 'Hierdie verslag is gekanselleer. Net wat dit gelees het, is gehef.',
  'insights.report.skipped': 'Hierdie maandelikse verslag is oorgeslaan omdat daar nie genoeg krediet was nie.',

  'insights.tabs.overview': 'Oorsig',
  'insights.tabs.topics': 'Onderwerpe',
  'insights.tabs.chatbot': 'Kletsbot',
  'insights.tabs.stopped': 'Waar gesprekke gestop het',
  'insights.tabs.gaps': 'Gapings',
  'insights.tabs.demand': 'Vraag en bemarking',
  'insights.tabs.improvements': 'Verbeterings',
  'insights.tabs.chats': 'Gesprekke',
  'insights.tabs.questions': 'Jou vrae',

  'insights.filter.aria': 'Filters',
  'insights.filter.from': 'Van',
  'insights.filter.to': 'Tot',
  'insights.filter.channel': 'Kanaal',
  'insights.filter.topic': 'Onderwerp',
  'insights.filter.customerType': 'Kliënt',
  'insights.filter.outcome': 'Uitkoms',
  'insights.filter.sentiment': 'Gevoel',
  'insights.filter.anyChannel': 'Enige kanaal',
  'insights.filter.anyTopic': 'Enige onderwerp',
  'insights.filter.anyCustomerType': 'Enigiemand',
  'insights.filter.anyOutcome': 'Enige uitkoms',
  'insights.filter.anySentiment': 'Enige gevoel',
  'insights.filter.showing': 'Wys {{shown}} van {{total}} gesprekke',
  'insights.filter.refs': 'Net die {{count}} gesprekke agter \'n bevinding',
  'insights.filter.clear': 'Maak filters skoon',

  'insights.seeChats.one': 'Sien die gesprek',
  'insights.seeChats.other': 'Sien {{count}} gesprekke',
  'insights.empty.filtered': 'Geen gesprekke pas by hierdie filters nie.',
  'insights.empty.topics': 'Nog geen onderwerpe nie: geen gesprek wat by hierdie filters pas, is gelees nie.',

  'insights.kpi.chats': 'Gesprekke',
  'insights.kpi.people': 'Mense',
  'insights.kpi.newPeople': '{{count}} nuut',
  'insights.kpi.answeredByBot': 'Deur die kletsbot opgelos',
  'insights.kpi.answeredByBotHint': 'Van {{count}} gesprekke gelees',
  'insights.kpi.handedToTeam': 'Aan die span oorgegee',
  'insights.kpi.unanswered': 'Onbeantwoord gelaat',
  'insights.kpi.unansweredHint': 'Die kliënt het die laaste woord gehad',
  'insights.kpi.firstReply': 'Eerste antwoord deur die span',
  'insights.kpi.firstReplyHint': 'Middelste waarde',
  'insights.kpi.buying': 'Gereed om te koop of in te skryf',
  'insights.kpi.buyingHint': '{{count}} sterk',
  'insights.kpi.read': 'Gesprekke deur KI gelees',
  'insights.kpi.readHint': 'Met \'n kliënt se boodskap',
  'insights.kpi.sampled': '\'n Regverdige steekproef',
  'insights.kpi.before': '{{change}} teenoor vorige',
  'insights.kpi.filtered': 'Hierdie syfers is vir die gesprekke wat jou filters wys, so daar is geen vergelyking met die vorige tydperk nie.',

  'insights.overview.summary': 'Opsomming',
  'insights.overview.highlights': 'Hoogtepunte',
  'insights.overview.perDay': 'Gesprekke per dag',
  'insights.overview.perDayNote': 'Gesprekke met \'n boodskap op daardie dag, en hoeveel daarvan mense was wat vir die eerste keer geskryf het.',
  'insights.overview.chats': 'Gesprekke',
  'insights.overview.newPeople': 'Nuwe mense',
  'insights.overview.heat': 'Wanneer gesprekke begin',
  'insights.overview.heatNote': 'Per weekdag en uur, in jou tydsone. Donkerder beteken meer gesprekke; die besigste ure wys hul getal.',
  'insights.overview.heatDay': 'Dag',
  'insights.overview.heatCell': '{{day}} {{hour}}:00, {{count}} gesprekke',
  'insights.overview.heatFew': 'Minder',
  'insights.overview.heatMany': 'Meer (tot {{count}})',
  'insights.overview.channels': 'Kanale',
  'insights.overview.nextSteps': 'Volgende stappe',

  'insights.topics.title': 'Waaroor mense gevra het',
  'insights.topics.note': 'Onderwerpe word uit jou eie kliënte se woorde bepaal. \'n Gesprek kan oor meer as een gaan.',
  'insights.topics.sampled': 'Uit die steekproef van gesprekke wat gelees is. \'n Gesprek kan oor meer as een onderwerp gaan.',
  'insights.topics.table': 'Onderwerpe',
  'insights.topics.topic': 'Onderwerp',
  'insights.topics.chats': 'Gesprekke',
  'insights.topics.share': 'Deel',
  'insights.topics.answered': 'Volledig beantwoord',
  'insights.topics.handedOver': 'Aan die span oorgegee',
  'insights.topics.trend': 'Per dag',
  'insights.topics.trendAria': 'Gesprekke oor {{name}} per dag',
  'insights.topics.byType': 'Onderwerpe per kliënt',
  'insights.topics.byTypeNote': 'Hoeveel gesprekke van elke soort kliënt oor elke onderwerp gegaan het.',

  'insights.answered.yes': 'Beantwoord',
  'insights.answered.partly': 'Gedeeltelik beantwoord',
  'insights.answered.no': 'Nie beantwoord nie',
  'insights.bot.questions': 'Vrae beantwoord',
  'insights.bot.questionsNote': '{{count}} vrae wat kliënte in die gewysde gesprekke gevra het.',
  'insights.bot.split': '{{yes}}% beantwoord, {{partly}}% gedeeltelik, {{no}}% nie beantwoord nie.',
  'insights.bot.outcomes': 'Hoe gesprekke geëindig het',
  'insights.bot.flows': 'Vloeie',
  'insights.bot.flowsNote': 'Vir die hele tydperk: hoe dikwels elke vloei begin het, wat dit begin het en hoe ver lopies gekom het.',
  'insights.bot.noFlows': 'Geen vloei het in hierdie tydperk geloop nie.',
  'insights.bot.flow': 'Vloei',
  'insights.bot.starts': 'Begin',
  'insights.bot.startedBy': 'Begin deur',
  'insights.bot.finished': 'Voltooi',
  'insights.bot.handedOver': 'Oorgegee',
  'insights.bot.stopped': 'Gestop',
  'insights.bot.funnel': 'Hoe ver lopies van {{name}} gekom het',
  'insights.bot.reached': 'Lopies wat hierdie stap bereik het',
  'insights.bot.funnelNote': 'Elke stap in die volgorde waarin die vloei dit doen. \'n Groot daling tussen twee stappe wys waar mense weggaan.',
  'insights.bot.ai': 'KI-antwoorde',
  'insights.bot.aiNote': 'Vir die hele tydperk: antwoorde wat jou kletsbot uit jou kennisbasis geskryf het.',
  'insights.bot.aiGiven': 'Antwoorde gegee',
  'insights.bot.aiSure': 'Seker van die antwoord',
  'insights.bot.aiNotSure': 'Nie seker nie',
  'insights.bot.notSureQuestions': 'Vrae waaroor dit nie seker was nie',
  'insights.bot.notSureLine': '{{question}} ({{count}} keer)',
  'insights.bot.deep': 'Deeglike hersiening van antwoorde',
  'insights.bot.deepNote': 'Die nuutste deeglike hersiening in hierdie tydperk, uit Vloeie, Afrigter.',
  'insights.bot.assessment': 'Hoe die kletsbot gevaar het',
  'insights.bot.strengths': 'Wat goed gegaan het',
  'insights.bot.weaknesses': 'Wat om reg te maak',
  'insights.start.keyword': 'Sleutelwoord',
  'insights.start.intent': 'Wat hulle gevra het',
  'insights.start.greeting': 'Groet',
  'insights.start.first_contact': 'Eerste boodskap',
  'insights.start.default': 'Verstek',
  'insights.start.offer': 'Knoppie aangebied',
  'insights.start.switch': 'Vloei verander',
  'insights.start.api': 'API',

  'insights.stopped.title': 'Waarom gesprekke gestop het',
  'insights.stopped.note': 'Gesprekke wat geëindig het sonder wat die kliënt kom soek het, gegroepeer volgens waarom.',
  'insights.stopped.none': 'Geen gesprek wat by hierdie filters pas, het voortydig gestop nie.',
  'insights.stopped.reason': 'Waarom',
  'insights.stopped.after': 'Gestop na',
  'insights.stopped.steps': 'Vloeistappe wat niemand beantwoord het nie',
  'insights.stopped.stepsNote': 'Vir die hele tydperk: die stap waar elke lopie gestop het omdat die kliënt nooit op sy vraag geantwoord het nie.',
  'insights.stopped.noSteps': 'Geen lopie het in hierdie tydperk by \'n vraag gestop nie.',
  'insights.stopped.step': 'Stap',
  'insights.stopped.runs': 'Lopies',

  'insights.gaps.title': 'Vrae wat jou kletsbot nie kon beantwoord nie',
  'insights.gaps.note': 'Vrae wat nie of net gedeeltelik beantwoord is nie, gegroepeer volgens tema, met jou kliënte se eie woorde.',
  'insights.gaps.none': 'Elke vraag in hierdie gesprekke is beantwoord.',
  'insights.gaps.theme': 'Tema',
  'insights.gaps.asked': 'Gevra',
  'insights.gaps.answer': 'Beantwoord dit',
  'insights.gaps.answerNote': 'Beantwoord dit voeg \'n regstelling by jou kennisbasis. Jou kletsbot antwoord dadelik daaruit.',
  'insights.gaps.missing': 'Wat ontbreek, en waarom',
  'insights.gaps.missingLine': '{{theme}} ({{count}} keer)',
  'insights.gaps.documents': 'Dokumente om volgende by te voeg',
  'insights.gaps.wouldAnswer': 'Sal sowat {{count}} vrae beantwoord',
  'insights.gaps.outline': 'Wat om daarin te sit',
  'insights.gaps.questionsItAnswers': 'Vrae wat dit sal beantwoord',
  'insights.gaps.copy': 'Kopieer uiteensetting',
  'insights.gaps.copied': 'Gekopieer',
  'insights.gaps.addDocument': 'Voeg dokument by',

  'insights.demand.products': 'Produkte en dienste waaroor gevra is',
  'insights.demand.productsNote': 'Uit die gesprekke wat gelees is. \'n Gesprek kan meer as een noem.',
  'insights.demand.noProducts': 'Geen produk of diens is in hierdie gesprekke genoem nie.',
  'insights.demand.unmet': 'Gevra, maar nie aangebied nie',
  'insights.demand.unmetNote': 'Dinge wat kliënte wou hê wat jy nog nie aanbied nie.',
  'insights.demand.marketing': 'Bemarkingsidees',
  'insights.demand.audience': 'Wie: {{value}}',
  'insights.demand.channel': 'Waar: {{value}}',
  'insights.demand.timing': 'Wanneer: {{value}}',
  'insights.demand.opportunities': 'Geleenthede',
  'insights.demand.risks': 'Risiko\'s',
  'insights.demand.complaints': 'Klagtes',
  'insights.demand.complaintsNote': '{{count}} gesprekke met \'n klagte.',
  'insights.demand.leads': 'Gereed om te koop of in te skryf',
  'insights.demand.leadsNote': 'Mense wat gewys het dat hulle wil koop, bespreek of inskryf. Sterk tekens eerste.',
  'insights.demand.noLeads': 'Niemand in hierdie gesprekke het gewys dat hulle gereed was om te koop nie.',
  'insights.demand.signal': 'Teken',
  'insights.demand.wants': 'Wil hê',
  'insights.demand.tag': 'Merk hierdie mense',
  'insights.demand.tagTitle': 'Merk hierdie mense in jou kontakte',
  'insights.demand.tagIntro': '{{count}} van hulle is in jou kontakte. Gee hulle \'n etiket, en stuur hulle dan \'n uitsending volgens daardie etiket.',
  'insights.demand.tagWithout': '{{count}} kan nie gemerk word nie, omdat hulle op jou webwerf geskryf het en nie in jou kontakte is nie.',
  'insights.demand.tagName': 'Etiket',
  'insights.demand.tagHint': 'Tot 64 karakters, byvoorbeeld: Inskrywings 2027.',
  'insights.demand.tagSave': 'Merk {{count}} kontakte',
  'insights.demand.tagged': '{{count}} kontakte gemerk met {{tag}}.',

  'insights.improve.title': 'Verbeterings',
  'insights.improve.none': 'Geen verbeterings is in hierdie verslag voorgestel nie.',
  'insights.improve.allAreas': 'Almal',
  'insights.improve.what': 'Wat om te doen',
  'insights.improve.area': 'Gebied',
  'insights.improve.impact': 'Impak',
  'insights.improve.effort': 'Moeite',
  'insights.improve.evidence': 'Waarom: {{value}}',
  'insights.area.chatbot': 'Kletsbot',
  'insights.area.flows': 'Vloeie',
  'insights.area.knowledge': 'Kennisbasis',
  'insights.area.team': 'Span',
  'insights.area.hours': 'Ure',
  'insights.area.other': 'Ander',
  'insights.level.high': 'Hoog',
  'insights.level.medium': 'Medium',
  'insights.level.low': 'Laag',

  'insights.chats.title': 'Gesprekke',
  'insights.chats.note': '{{count}} gesprekke, nuutste eerste. Maak een oop om dit in die inkassie te lees.',
  'insights.chats.csv': 'Laai CSV af',
  'insights.chats.ref': 'Verw.',
  'insights.chats.date': 'Datum',
  'insights.chats.person': 'Persoon',
  'insights.chats.channel': 'Kanaal',
  'insights.chats.summary': 'Waaroor dit gegaan het',
  'insights.chats.outcome': 'Uitkoms',
  'insights.chats.open': 'Maak gesprek oop',
  'insights.chats.unknown': 'Onbekend',
  'insights.chats.flows': 'Vloeie: {{names}}',
  'insights.chats.notRead': 'Nie deur KI gelees nie (geen boodskap van \'n kliënt nie, of buite die steekproef).',

  'insights.questions.none': 'Hierdie verslag het geen vrae van jou eie nie.',
  'insights.questions.noAnswer': 'Daar was geen antwoord op hierdie vraag nie.',
  'insights.questions.counts': 'In die gewysde gesprekke: {{yes}} ja, {{no}} nee, {{unclear}} onduidelik.',

  'insights.outcome.resolved_by_bot': 'Deur die kletsbot opgelos',
  'insights.outcome.resolved_by_team': 'Deur die span opgelos',
  'insights.outcome.waiting_for_team': 'Wag vir die span',
  'insights.outcome.abandoned': 'Kliënt het weggegaan',
  'insights.outcome.info_only': 'Net inligting',
  'insights.outcome.spam': 'Gemorspos',
  'insights.sentiment.positive': 'Positief',
  'insights.sentiment.neutral': 'Neutraal',
  'insights.sentiment.negative': 'Negatief',
  'insights.buying.none': 'Geen',
  'insights.buying.some': 'Bietjie belangstelling',
  'insights.buying.strong': 'Sterk',
  'insights.channel.whatsapp': 'WhatsApp',
  'insights.channel.web': 'Webwerfklets',
  'insights.channel.messenger': 'Messenger',
  'insights.channel.instagram': 'Instagram',
  'insights.channel.email': 'E-pos',
  'insights.channel.sms': 'SMS',
  'insights.channel.voice': 'Oproep',

  'insights.csv.ref': 'Verw.',
  'insights.csv.date': 'Datum',
  'insights.csv.channel': 'Kanaal',
  'insights.csv.person': 'Persoon',
  'insights.csv.returning': 'Het voorheen geskryf',
  'insights.csv.topics': 'Onderwerpe',
  'insights.csv.products': 'Produkte en dienste',
  'insights.csv.customerType': 'Kliënt',
  'insights.csv.outcome': 'Uitkoms',
  'insights.csv.sentiment': 'Gevoel',
  'insights.csv.stopReason': 'Waarom dit gestop het',
  'insights.csv.stoppedAfter': 'Gestop na',
  'insights.csv.buying': 'Gereed om te koop',
  'insights.csv.buyingDetail': 'Wil hê',
  'insights.csv.unanswered': 'Onbeantwoord gelaat',
  'insights.csv.takenOver': 'Aan die span oorgegee',
  'insights.csv.firstReply': 'Eerste antwoord (minute)',
  'insights.csv.flows': 'Vloeie',
  'insights.csv.summary': 'Opsomming',
  'insights.csv.quote': 'Kliënt se woorde',
  'insights.csv.yes': 'Ja',
  'insights.csv.no': 'Nee',
  'insights.stage.done': 'Klaar',
  'insights.stage.now': 'Besig',
  'insights.stage.later': 'Wag',
  'insights.stage.of': '{{done}} van {{total}}',
  'insights.running.overall': '{{pct}}% klaar',
  'insights.gaps.suggestFlows': 'Stel vloeie hiervoor voor'
};

export default insights;
